import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ChevronRight, ArrowRight } from "lucide-react";
import { api, fetchProduct } from "../lib/api";
import useSeo from "../hooks/useSeo";

const GripDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [grip, setGrip] = useState(null);
  const [product, setProduct] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api
      .get(`/grips/${slug}`)
      .then((r) => setGrip(r.data))
      .catch((err) => {
        if (err?.response?.status === 404) {
          setNotFound(true);
        } else {
          console.warn("[grip-detail] load failed", err);
        }
      });
    fetchProduct().then(setProduct).catch((err) => {
      console.warn("[grip-detail] product load failed", err);
    });
  }, [slug]);

  useSeo({
    title: grip ? `${grip.name} · ${grip.tagline}` : "Grip Pattern",
    description: grip
      ? `${grip.name} — ${grip.tagline} ${grip.subline} ${grip.description}`
      : undefined,
    path: `/grips/${slug}`,
    jsonLdId: "grip-detail-jsonld",
    jsonLd: grip
      ? {
          "@context": "https://schema.org",
          "@type": "Product",
          name: `${grip.name} grip pattern · Pritch Grip Sock`,
          description: grip.description,
          sku: `PRITCH-GRIP-${grip.slug.toUpperCase()}`,
          brand: { "@type": "Brand", name: "Pritch" },
          category: "Football Grip Socks",
          offers: {
            "@type": "Offer",
            priceCurrency: "GBP",
            price: (product?.price || 18).toFixed(2),
            availability: "https://schema.org/InStock",
            url: `https://pritch.fc/grips/${grip.slug}`,
          },
        }
      : undefined,
  });

  if (notFound) {
    return (
      <div className="container-x py-32 text-center" data-testid="grip-not-found">
        <p className="overline mb-3">Not Found</p>
        <h1 className="font-display uppercase text-4xl tracking-tight font-bold">
          That grip doesn&apos;t exist.
        </h1>
        <button onClick={() => navigate("/grips")} className="btn-primary mt-6">
          See all grips
        </button>
      </div>
    );
  }

  if (!grip) {
    return <div className="container-x py-32 text-mute" data-testid="grip-loading">Loading…</div>;
  }

  // Find an adjacent grip for the "Next grip" CTA.
  const grips = product?.grips || [];
  const idx = grips.findIndex((g) => g.slug === grip.slug);
  const next = grips[(idx + 1) % grips.length];
  const prev = grips[(idx - 1 + grips.length) % grips.length];

  return (
    <div className="bg-chalk" data-testid="grip-detail-page">
      <div className="container-x py-4 text-[11px] tracking-[0.18em] uppercase text-mute flex items-center gap-2">
        <button onClick={() => navigate("/")} className="hover:text-ink">Home</button>
        <ChevronRight size={12} />
        <button onClick={() => navigate("/grips")} className="hover:text-ink">Grips</button>
        <ChevronRight size={12} />
        <span className="text-ink">{grip.name}</span>
      </div>

      <section className="container-x py-12 md:py-20 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-7">
          <p className="mono text-xs text-mute mb-4">{grip.no} · The Grip System</p>
          <h1 className="font-display uppercase text-5xl md:text-7xl tracking-tight font-bold leading-[0.92]">
            {grip.name}.
          </h1>
          <p className="mt-8 text-xl text-ink leading-snug max-w-xl">
            <span className="font-semibold">{grip.tagline}</span>{" "}
            <span className="text-mute">{grip.subline}</span>
          </p>
          <p className="mt-6 max-w-xl text-mute leading-relaxed">{grip.description}</p>

          <div className="mt-10 flex flex-wrap gap-3">
            <Link
              to={`/product/pritch-grip-sock?g=${grip.slug}`}
              className="btn-primary"
              data-testid="grip-detail-shop-cta"
            >
              Shop with {grip.name.replace("Pritch ", "")} <ArrowRight size={14} className="ml-2" />
            </Link>
            <Link to="/grips" className="btn-secondary">
              All 8 grips
            </Link>
          </div>
        </div>

        <div className="md:col-span-5">
          <div className="aspect-square border border-line bg-white p-10 flex items-center justify-center">
            <div className="text-center">
              <p className="mono text-xs text-mute">{grip.no}</p>
              <h2 className="wordmark text-4xl md:text-5xl mt-3 uppercase font-bold">{grip.name.replace("Pritch ", "")}</h2>
              <p className="subwordmark text-xs md:text-sm text-mute mt-4">{grip.tagline}</p>
              <p className="subwordmark text-xs md:text-sm text-mute">{grip.subline}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="container-x py-16 border-t border-line">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-10">
          <div className="md:col-span-7">
            <p className="overline mb-3">Pair {grip.name.replace("Pritch ", "")} with a colourway</p>
            <h2 className="font-display uppercase text-3xl md:text-4xl tracking-tight font-bold leading-[0.95]">
              12 colourways. One grip pick.
            </h2>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-px bg-line border border-line">
          {(product?.colourways || []).map((c) => {
            const isDark = c.slug === "blackout-white";
            return (
              <Link
                key={c.slug}
                to={`/product/pritch-grip-sock?c=${c.slug}&g=${grip.slug}`}
                className="bg-white aspect-square relative overflow-hidden block"
                style={{ backgroundColor: c.hex }}
                data-testid={`grip-detail-colour-${c.slug}`}
              >
                {c.image ? (
                  <img src={c.image} alt={c.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <p
                      className={`wordmark uppercase text-lg ${
                        isDark ? "text-white" : "text-ink"
                      }`}
                    >
                      {c.name.split(" ")[0]}
                    </p>
                  </div>
                )}
                <div className="absolute bottom-2 left-2 right-2 bg-white/85 backdrop-blur-sm border border-line px-2 py-1">
                  <p className="mono text-[10px] tracking-widest uppercase text-ink truncate">{c.name}</p>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      <section className="container-x py-16 border-t border-line">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-line border border-line">
          {prev && prev.slug !== grip.slug && (
            <Link
              to={`/grips/${prev.slug}`}
              className="bg-white p-8 hover:bg-bone transition-colors"
              data-testid="grip-prev"
            >
              <p className="mono text-xs text-mute">← Previous</p>
              <p className="font-display uppercase text-2xl tracking-tight font-bold mt-2">{prev.name}</p>
              <p className="text-sm text-mute mt-1">{prev.tagline}</p>
            </Link>
          )}
          {next && next.slug !== grip.slug && (
            <Link
              to={`/grips/${next.slug}`}
              className="bg-white p-8 hover:bg-bone transition-colors text-right"
              data-testid="grip-next"
            >
              <p className="mono text-xs text-mute">Next →</p>
              <p className="font-display uppercase text-2xl tracking-tight font-bold mt-2">{next.name}</p>
              <p className="text-sm text-mute mt-1">{next.tagline}</p>
            </Link>
          )}
        </div>
      </section>
    </div>
  );
};

export default GripDetailPage;
